<template>
    <div>
        <!-- Page Banner -->
        <PageBanner pageTitle="Blog" />
        
        <!-- Blog List Section -->
        <div ref="sectionRef" class="container ptb-115">
            <div class="row justify-content-center">
                <!-- Loading State -->
                <div v-if="loading" class="col-12 text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-3 text-para">Loading blog posts...</p>
                </div>
                
                <!-- Blog Posts -->
                <div 
                    v-else-if="paginatedPosts.length" 
                    v-for="(post, index) in paginatedPosts" 
                    :key="post.id || index"
                    class="col-xl-4 col-md-6"
                >
                    <div class="blog-card style-three position-relative img-hover-scale mb-30">
                        <div class="blog-img position-relative img-scale overflow-hidden round-20 mb-30">
                            <router-link :to="`/blog/${post.slug}`">
                                <img 
                                    :src="post.banner_image" 
                                    :alt="post.title" 
                                    class="round-20 transition w-100" 
                                />
                            </router-link>
                        </div>
                        <router-link
                            v-if="post.category"
                            :to="`/blog/category/${post.category_slug || 'general'}`"
                            class="blog-category fs-14 bg-optional text-white round-oval transition"
                        >
                            {{ post.category }}
                        </router-link>
                        <h3 class="fs-20 fs-xxl-24 fw-semibold mb-12">
                            <router-link 
                                :to="`/blog/${post.slug}`" 
                                class="text-title link-hover-primary transition"
                            >
                                {{ post.title }}
                            </router-link>
                        </h3>
                        <ul class="blog-metainfo list-unstyled mb-0">
                            <li class="blog-author position-relative text_secondary" v-if="post.author || post.author_name">
                                <span
                                    class="d-flex flex-column align-items-center justify-content-center rounded-circle bg-title"
                                >
                                    <i class="ri-user-line"></i>
                                </span>
                                By:
                                <span class="text_secondary">
                                    {{ post.author || post.author_name }}
                                </span>
                            </li>
                            <li class="position-relative" v-if="post.published_at || post.created_at">
                                <span class="text-para">
                                    <i class="ri-calendar-line me-5"></i>
                                    {{ formatDate(post.published_at || post.created_at) }}
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
                
                <!-- No Posts -->
                <div v-else class="col-12 text-center py-5">
                    <h3 class="mb-20">No blog posts found</h3>
                    <p class="text-para">Check back later for new content.</p>
                </div>
            </div>

            <!-- Pagination -->
            <ul class="page-nav pagination justify-content-center mt-4 mb-0" v-if="totalPages > 1">
                <li class="page-item">
                    <button
                        class="page-link d-flex flex-column align-items-center justify-content-center"
                        @click="previousPage"
                        :disabled="currentPage === 1"
                        aria-label="Previous"
                    >
                        <i class="ri-arrow-left-s-line"></i>
                    </button>
                </li>
                <li class="page-item" v-for="page in displayPages" :key="page">
                    <button
                        class="page-link d-flex flex-column align-items-center justify-content-center"
                        :class="{ active: currentPage === page }"
                        @click="goToPage(page)"
                    >
                        {{ page.toString().padStart(2, '0') }}
                    </button>
                </li>
                <li class="page-item">
                    <button
                        class="page-link d-flex flex-column align-items-center justify-content-center"
                        @click="nextPage"
                        :disabled="currentPage === totalPages"
                        aria-label="Next"
                    >
                        <i class="ri-arrow-right-s-line"></i>
                    </button>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import PageBanner from '@/components/Common/PageBanner.vue'
import defaultBlogImage from '@/assets/img/blog/blog-1.jpg'

export default {
    name: 'Blog',
    components: {
        PageBanner
    },
    data() {
        return {
            blogPosts: [],
            loading: true,
            currentPage: 1,
            postsPerPage: 6,
            totalPosts: 0,
            defaultBlogImage
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalPosts / this.postsPerPage)
        },
        paginatedPosts() {
            const startIndex = (this.currentPage - 1) * this.postsPerPage
            const endIndex = startIndex + this.postsPerPage
            return this.blogPosts.slice(startIndex, endIndex)
        },
        displayPages() {
            // Show max 5 page numbers
            const pages = []
            let start = Math.max(1, this.currentPage - 2)
            let end = Math.min(this.totalPages, start + 4)
            
            if (end - start < 4) {
                start = Math.max(1, end - 4)
            }
            
            for (let i = start; i <= end; i++) {
                pages.push(i)
            }
            return pages
        }
    },
    async mounted() {
        await this.loadBlogPosts()
        
        // Set page title
        document.title = 'Blog - ' + (this.siteDefaultsStore?.getSiteName() || 'DPMC')
    },
    watch: {
        currentPage() {
            this.scrollToSection()
        }
    },
    methods: {
        async loadBlogPosts() {
            this.loading = true
            
            try {
                const response = await this.$api.get('public/blogs')
                if (response.data && response.data.status === 'success') {
                    this.blogPosts = response.data.data || []
                    this.totalPosts = this.blogPosts.length
                }
            } catch (error) {
                console.error('Failed to load blog posts:', error)
                this.blogPosts = []
                this.totalPosts = 0
            } finally {
                this.loading = false
            }
        },
        
        formatDate(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            const options = { year: 'numeric', month: 'short', day: 'numeric' }
            return date.toLocaleDateString('en-US', options)
        },
        
        scrollToSection() {
            this.$nextTick(() => {
                if (this.$refs.sectionRef) {
                    this.$refs.sectionRef.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            })
        },
        
        previousPage() {
            if (this.currentPage > 1) {
                this.currentPage--
            }
        },
        
        nextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage++
            }
        },
        
        goToPage(page) {
            this.currentPage = page
        }
    }
}
</script>

<style scoped>
.blog-card.style-three {
    transition: transform 0.3s ease;
}

.blog-card.style-three:hover {
    transform: translateY(-10px);
}

.blog-img {
    height: 280px;
    overflow: hidden;
}

.blog-img img {
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
}

.blog-card:hover .blog-img img {
    transform: scale(1.1);
}

.blog-category {
    display: inline-block;
    padding: 6px 20px;
    margin-bottom: 15px;
    text-decoration: none;
}

.blog-category:hover {
    background: var(--bs-primary) !important;
}

.blog-metainfo {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    align-items: center;
}

.blog-author {
    display: flex;
    align-items: center;
    gap: 8px;
}

.blog-author span {
    width: 30px;
    height: 30px;
    font-size: 14px;
}

.page-nav.pagination {
    gap: 10px;
}

.page-nav .page-link {
    width: 45px;
    height: 45px;
    border-radius: 10px;
    border: 1px solid #e5e5e5;
    background: white;
    color: #333;
    transition: all 0.3s ease;
}

.page-nav .page-link:hover,
.page-nav .page-link.active {
    background: var(--bs-primary);
    color: white;
    border-color: var(--bs-primary);
}

.page-nav .page-link:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>

